import { supabaseCoreClient } from '../supabase';
import { queryClient } from '../constants.js';

/**
 * Relation dependency graph.
 *
 * A realtime change arrives for a base table (e.g. `public.submission`), but the UI reads
 * derived relations (`submission_view`, `submission_deep_view`,
 * `rep_performance_summary_v`, ...). This module loads the view dependency edges from
 * `core_crm.get_relation_dependencies()` once per session and answers:
 *
 *   "which relations must be considered stale when `public.submission` changes?"
 */

export interface RelationDependencyRow {
  dependent_schema: string;
  dependent_name: string;
  source_schema: string;
  source_name: string;
}

/** Qualified relation name, e.g. `public.submission`. */
export type QualifiedRelation = string;

export const qualify = (schema: string, relation: string): QualifiedRelation => `${schema}.${relation}`;

const DEPENDENCY_QUERY_KEY = ['realtime', 'relation-dependencies'];
// The graph only changes when views are created/altered (a deploy or a schema-admin action),
// so a long stale time is fine — a page reload picks up a newer graph.
const DEPENDENCY_STALE_TIME_MS = 30 * 60 * 1000;

/** source relation -> relations that read from it (direct edges only) */
let directDependents: Map<QualifiedRelation, Set<QualifiedRelation>> | null = null;
/** memoized transitive closure per source, including the source itself */
const closureCache = new Map<QualifiedRelation, Set<QualifiedRelation>>();
let loadPromise: Promise<void> | null = null;

const buildIndex = (rows: RelationDependencyRow[]): Map<QualifiedRelation, Set<QualifiedRelation>> => {
  const index = new Map<QualifiedRelation, Set<QualifiedRelation>>();
  rows.forEach((row) => {
    const source = qualify(row.source_schema, row.source_name);
    const dependent = qualify(row.dependent_schema, row.dependent_name);
    const dependents = index.get(source) ?? new Set<QualifiedRelation>();
    dependents.add(dependent);
    index.set(source, dependents);
  });
  return index;
};

/**
 * Fallback used before the graph has loaded (or if the RPC fails): the naming convention
 * every metadata-generated view follows.
 */
const conventionalDependents = (relation: QualifiedRelation): Set<QualifiedRelation> =>
  new Set([
    relation,
    `${relation}_view`,
    `${relation}_deep_view`,
    `${relation}_calculated_fields`
  ]);

/**
 * Loads the dependency graph. Safe to call repeatedly — the fetch is deduplicated and cached.
 */
export const loadRelationDependencies = async (): Promise<void> => {
  if (loadPromise) return loadPromise;

  loadPromise = queryClient
    .fetchQuery({
      queryKey: DEPENDENCY_QUERY_KEY,
      staleTime: DEPENDENCY_STALE_TIME_MS,
      gcTime: DEPENDENCY_STALE_TIME_MS,
      queryFn: async (): Promise<RelationDependencyRow[]> => {
        const { data, error } = await supabaseCoreClient.rpc('get_relation_dependencies');
        if (error) throw error;
        return data ?? [];
      }
    })
    .then((rows) => {
      directDependents = buildIndex(rows);
      closureCache.clear();
    })
    .catch((error) => {
      // Not fatal: getAffectedRelations falls back to the *_view / *_deep_view convention.
      console.error('[liveSync] Failed to load relation dependency graph:', error);
      loadPromise = null;
    });

  return loadPromise;
};

/**
 * All relations whose cached data may be stale after `schema.relation` changed —
 * the relation itself plus every view that (transitively) reads from it.
 */
export const getAffectedRelations = (schema: string, relation: string): Set<QualifiedRelation> => {
  const root = qualify(schema, relation);

  if (!directDependents) {
    // Kick off the load so subsequent events get the real graph.
    void loadRelationDependencies();
    return conventionalDependents(root);
  }

  const cached = closureCache.get(root);
  if (cached) return cached;

  const affected = new Set<QualifiedRelation>([root]);
  const queue: QualifiedRelation[] = [root];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const dependents = directDependents.get(current);
    if (!dependents) continue;
    dependents.forEach((dependent) => {
      if (affected.has(dependent)) return; // also breaks cycles
      affected.add(dependent);
      queue.push(dependent);
    });
  }

  closureCache.set(root, affected);
  return affected;
};

/** Test/debug helper — drops the cached graph so the next lookup reloads it. */
export const resetRelationDependencies = (): void => {
  directDependents = null;
  closureCache.clear();
  loadPromise = null;
};
